/**
 * Advanced Data Validation & Quality Dashboard
 * DHIS2-inspired data quality interface with validation rules, outlier detection,
 * completeness tracking, and actionable quality reports
 */
import React, { useState } from 'react';
import {
  Box, Card, CardContent, Typography, Grid, Tabs, Tab, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Paper, Chip, LinearProgress,
  Button, Alert, TextField, FormControl, InputLabel, Select, MenuItem,
  IconButton, Tooltip, Checkbox, Badge, Divider, CircularProgress,
} from '@mui/material';
import {
  CheckCircle, Warning, Error as ErrorIcon, Info, Refresh, Download,
  Flag, Visibility, PlayArrow, FilterList, TrendingUp, TrendingDown,
  VerifiedUser, DataUsage, Rule, BugReport,
} from '@mui/icons-material';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip,
  ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line, Legend,
  RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
} from 'recharts';
import { QUALITY_COLORS, CHART_COLORS, formatNumber } from '../../theme/dashboardTheme';

interface QualityDimension {
  name: string;
  score: number;
  maxScore: number;
  issues: number;
  status: 'good' | 'warning' | 'critical';
}

interface ValidationRule {
  id: string;
  name: string;
  description: string;
  type: 'required' | 'range' | 'consistency' | 'uniqueness' | 'format' | 'custom';
  severity: 'error' | 'warning' | 'info';
  field: string;
  violations: number;
  totalChecked: number;
  lastRun?: string;
}

interface OutlierRecord {
  id: string;
  orgUnit: string;
  dataElement: string;
  period: string;
  value: number;
  mean: number;
  stdDev: number;
  zScore: number;
  flaggedForReview: boolean;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => (
  <div hidden={value !== index}>{value === index && <Box sx={{ pt: 2 }}>{children}</Box>}</div>
);

const AdvancedDataValidation: React.FC = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [outlierAlgorithm, setOutlierAlgorithm] = useState('zscore');
  const [outlierThreshold, setOutlierThreshold] = useState(3);
  const [selectedRules, setSelectedRules] = useState<Set<string>>(new Set());

  // Quality dimensions data
  const qualityDimensions: QualityDimension[] = [
    { name: 'Completeness', score: 87, maxScore: 100, issues: 156, status: 'good' },
    { name: 'Accuracy', score: 92, maxScore: 100, issues: 43, status: 'good' },
    { name: 'Consistency', score: 74, maxScore: 100, issues: 287, status: 'warning' },
    { name: 'Timeliness', score: 68, maxScore: 100, issues: 12, status: 'warning' },
    { name: 'Uniqueness', score: 95, maxScore: 100, issues: 28, status: 'good' },
  ];

  const overallScore = Math.round(
    qualityDimensions.reduce((sum, d) => sum + d.score, 0) / qualityDimensions.length
  );

  // Radar chart data
  const radarData = qualityDimensions.map(d => ({
    dimension: d.name,
    score: d.score,
    fullMark: 100,
  }));

  // Validation rules
  const validationRules: ValidationRule[] = [
    { id: 'R001', name: 'Missing School Registration', description: 'Schools must have a registration number', type: 'required', severity: 'error', field: 'registration_number', violations: 23, totalChecked: 1537, lastRun: '2025-01-15' },
    { id: 'R002', name: 'Invalid Enrollment Count', description: 'Enrollment must be greater than 0 for active schools', type: 'range', severity: 'error', field: 'total_enrollment', violations: 8, totalChecked: 1537, lastRun: '2025-01-15' },
    { id: 'R003', name: 'Student in Multiple Schools', description: 'A student cannot be enrolled in more than one school', type: 'consistency', severity: 'error', field: 'student_id', violations: 45, totalChecked: 369702, lastRun: '2025-01-14' },
    { id: 'R004', name: 'Missing GPS Coordinates', description: 'Schools should have latitude and longitude recorded', type: 'required', severity: 'warning', field: 'latitude, longitude', violations: 156, totalChecked: 1537, lastRun: '2025-01-15' },
    { id: 'R005', name: 'Teacher-Student Ratio Outlier', description: 'T/S ratio should be between 1:10 and 1:100', type: 'range', severity: 'warning', field: 'student_teacher_ratio', violations: 67, totalChecked: 1537, lastRun: '2025-01-15' },
    { id: 'R006', name: 'Duplicate Student Records', description: 'Student ID must be unique within the system', type: 'uniqueness', severity: 'error', field: 'student_id', violations: 28, totalChecked: 369702, lastRun: '2025-01-14' },
    { id: 'R007', name: 'Gender Parity Extreme', description: 'GPI should be between 0.8 and 1.2', type: 'range', severity: 'info', field: 'gender_parity_index', violations: 34, totalChecked: 1537, lastRun: '2025-01-15' },
    { id: 'R008', name: 'Enrollment Exceeds Capacity', description: 'Total enrollment should not exceed school capacity', type: 'consistency', severity: 'warning', field: 'enrollment vs capacity', violations: 89, totalChecked: 1537, lastRun: '2025-01-15' },
    { id: 'R009', name: 'Missing Head Teacher', description: 'Active schools should have a head teacher assigned', type: 'required', severity: 'warning', field: 'head_teacher_name', violations: 42, totalChecked: 1537, lastRun: '2025-01-15' },
    { id: 'R010', name: 'Invalid Year Established', description: 'Year established should be between 1900 and current year', type: 'format', severity: 'info', field: 'year_established', violations: 12, totalChecked: 1537, lastRun: '2025-01-15' },
  ];

  // Outlier data
  const outliers: OutlierRecord[] = [
    { id: 'O1', orgUnit: 'Kisiwandui Primary', dataElement: 'Total Enrollment', period: '2024', value: 2847, mean: 240.5, stdDev: 180.3, zScore: 14.5, flaggedForReview: false },
    { id: 'O2', orgUnit: 'Mkanyageni Secondary', dataElement: 'Student-Teacher Ratio', period: '2024', value: 285, mean: 45.2, stdDev: 22.1, zScore: 10.8, flaggedForReview: true },
    { id: 'O3', orgUnit: 'Fumba Primary', dataElement: 'Dropout Rate', period: '2024', value: 34.5, mean: 5.2, stdDev: 3.8, zScore: 7.7, flaggedForReview: false },
    { id: 'O4', orgUnit: 'Kizimkazi Primary', dataElement: 'Total Enrollment', period: '2024', value: 12, mean: 240.5, stdDev: 180.3, zScore: -1.3, flaggedForReview: false },
    { id: 'O5', orgUnit: 'Nungwi Secondary', dataElement: 'Pass Rate', period: '2024', value: 98.7, mean: 62.3, stdDev: 15.4, zScore: 2.4, flaggedForReview: false },
    { id: 'O6', orgUnit: 'Bumbwini Primary', dataElement: 'Teacher Attendance', period: '2024', value: 23.1, mean: 88.5, stdDev: 8.2, zScore: -8.0, flaggedForReview: true },
  ];

  // Completeness data by district
  const completenessData = [
    { district: 'Mjini Magharibi', completeness: 94, schools: 245 },
    { district: 'Kaskazini A', completeness: 88, schools: 178 },
    { district: 'Kaskazini B', completeness: 82, schools: 156 },
    { district: 'Kusini', completeness: 91, schools: 198 },
    { district: 'Kati', completeness: 79, schools: 134 },
    { district: 'Magharibi A', completeness: 86, schools: 167 },
    { district: 'Magharibi B', completeness: 77, schools: 112 },
    { district: 'Wete', completeness: 85, schools: 143 },
    { district: 'Chake Chake', completeness: 90, schools: 109 },
    { district: 'Mkoani', completeness: 73, schools: 95 },
  ];

  const severityIcon = (severity: string) => {
    switch (severity) {
      case 'error': return <ErrorIcon sx={{ color: '#D32F2F', fontSize: 18 }} />;
      case 'warning': return <Warning sx={{ color: '#FF9302', fontSize: 18 }} />;
      default: return <Info sx={{ color: '#147CD7', fontSize: 18 }} />;
    }
  };

  const getScoreColor = (score: number) => {
    if (score >= 90) return '#388E3C';
    if (score >= 70) return '#FFC324';
    if (score >= 50) return '#FF9302';
    return '#D32F2F';
  };

  const totalViolations = validationRules.reduce((sum, r) => sum + r.violations, 0);
  const errorCount = validationRules.filter(r => r.severity === 'error').reduce((sum, r) => sum + r.violations, 0);
  const warningCount = validationRules.filter(r => r.severity === 'warning').reduce((sum, r) => sum + r.violations, 0);

  return (
    <Box>
      {/* Overall Quality Score */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={3}>
          <Card sx={{ background: `linear-gradient(135deg, ${getScoreColor(overallScore)}20, ${getScoreColor(overallScore)}05)`, border: `2px solid ${getScoreColor(overallScore)}40` }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, textTransform: 'uppercase' }}>
                Overall Quality Score
              </Typography>
              <Box sx={{ position: 'relative', display: 'inline-flex', my: 2 }}>
                <CircularProgress
                  variant="determinate"
                  value={overallScore}
                  size={100}
                  thickness={6}
                  sx={{ color: getScoreColor(overallScore) }}
                />
                <Box sx={{ position: 'absolute', top: 0, left: 0, bottom: 0, right: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: getScoreColor(overallScore) }}>
                    {overallScore}
                  </Typography>
                </Box>
              </Box>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {overallScore >= 90 ? 'Excellent' : overallScore >= 70 ? 'Good' : overallScore >= 50 ? 'Needs Improvement' : 'Critical'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Dimension Cards */}
        {qualityDimensions.map((dim, idx) => (
          <Grid item xs={6} md={1.8} key={idx}>
            <Card variant="outlined">
              <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, fontSize: '0.65rem' }}>
                  {dim.name.toUpperCase()}
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 700, color: getScoreColor(dim.score), my: 0.5 }}>
                  {dim.score}%
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={dim.score}
                  sx={{
                    height: 4, borderRadius: 2, backgroundColor: '#E8EDF2',
                    '& .MuiLinearProgress-bar': { backgroundColor: getScoreColor(dim.score), borderRadius: 2 },
                  }}
                />
                <Typography variant="caption" sx={{ color: dim.issues > 100 ? '#D32F2F' : 'text.secondary', fontSize: '0.65rem' }}>
                  {dim.issues} issues
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} sx={{ mb: 1 }}>
        <Tab label={<Badge badgeContent={errorCount} color="error"><Box sx={{ pr: 2 }}>Validation Rules</Box></Badge>} icon={<Rule />} iconPosition="start" />
        <Tab label={<Badge badgeContent={outliers.length} color="warning"><Box sx={{ pr: 2 }}>Outlier Detection</Box></Badge>} icon={<BugReport />} iconPosition="start" />
        <Tab label="Completeness" icon={<DataUsage />} iconPosition="start" />
        <Tab label="Quality Radar" icon={<VerifiedUser />} iconPosition="start" />
      </Tabs>

      {/* Tab 1: Validation Rules */}
      <TabPanel value={activeTab} index={0}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Chip icon={<ErrorIcon />} label={`${errorCount} Errors`} color="error" variant="outlined" size="small" />
            <Chip icon={<Warning />} label={`${warningCount} Warnings`} color="warning" variant="outlined" size="small" />
            <Chip label={`${totalViolations} Total Violations`} variant="outlined" size="small" />
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button size="small" variant="contained" startIcon={<PlayArrow />} disabled={loading}>
              Run All Rules
            </Button>
            <Button size="small" variant="outlined" startIcon={<Download />}>
              Export Report
            </Button>
          </Box>
        </Box>

        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox size="small" />
                </TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Severity</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Rule</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Field</TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="right">Violations</TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="right">Checked</TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="right">Pass Rate</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Last Run</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {validationRules.map(rule => {
                const passRate = ((1 - rule.violations / rule.totalChecked) * 100);
                return (
                  <TableRow key={rule.id} hover>
                    <TableCell padding="checkbox">
                      <Checkbox size="small" />
                    </TableCell>
                    <TableCell>{severityIcon(rule.severity)}</TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>{rule.name}</Typography>
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>{rule.description}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip label={rule.field} size="small" variant="outlined" sx={{ fontSize: '0.7rem', fontFamily: 'monospace' }} />
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" sx={{ fontWeight: 600, color: rule.violations > 0 ? '#D32F2F' : '#388E3C' }}>
                        {rule.violations.toLocaleString()}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2">{rule.totalChecked.toLocaleString()}</Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1 }}>
                        <LinearProgress
                          variant="determinate"
                          value={passRate}
                          sx={{
                            width: 60, height: 6, borderRadius: 3, backgroundColor: '#E8EDF2',
                            '& .MuiLinearProgress-bar': { backgroundColor: getScoreColor(passRate), borderRadius: 3 },
                          }}
                        />
                        <Typography variant="caption" sx={{ fontWeight: 600, minWidth: 40 }}>
                          {passRate.toFixed(1)}%
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>{rule.lastRun}</Typography>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </TabPanel>

      {/* Tab 2: Outlier Detection */}
      <TabPanel value={activeTab} index={1}>
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Algorithm</InputLabel>
              <Select value={outlierAlgorithm} label="Algorithm" onChange={(e) => setOutlierAlgorithm(e.target.value)}>
                <MenuItem value="zscore">Z-Score</MenuItem>
                <MenuItem value="modified_zscore">Modified Z-Score</MenuItem>
                <MenuItem value="iqr">IQR (Interquartile Range)</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField
              fullWidth size="small" label="Threshold" type="number"
              value={outlierThreshold}
              onChange={(e) => setOutlierThreshold(Number(e.target.value))}
              inputProps={{ min: 1, max: 5, step: 0.5 }}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <Button fullWidth variant="contained" startIcon={<PlayArrow />} sx={{ height: '100%' }}>
              Detect Outliers
            </Button>
          </Grid>
        </Grid>

        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Organisation Unit</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Data Element</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Period</TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="right">Value</TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="right">Mean</TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="right">Std Dev</TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="right">Z-Score</TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="center">Review</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {outliers.map(o => (
                <TableRow key={o.id} hover sx={{ backgroundColor: Math.abs(o.zScore) > 5 ? '#FFE5E8' : Math.abs(o.zScore) > 3 ? '#FFF8E1' : 'transparent' }}>
                  <TableCell>{o.orgUnit}</TableCell>
                  <TableCell>{o.dataElement}</TableCell>
                  <TableCell>{o.period}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, color: Math.abs(o.zScore) > 5 ? '#D32F2F' : '#212934' }}>
                    {o.value.toLocaleString()}
                  </TableCell>
                  <TableCell align="right">{o.mean.toFixed(1)}</TableCell>
                  <TableCell align="right">{o.stdDev.toFixed(1)}</TableCell>
                  <TableCell align="right">
                    <Chip
                      label={o.zScore.toFixed(1)}
                      size="small"
                      sx={{
                        fontWeight: 600,
                        backgroundColor: Math.abs(o.zScore) > 5 ? '#D32F2F' : Math.abs(o.zScore) > 3 ? '#FF9302' : '#FFC324',
                        color: '#fff',
                      }}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title={o.flaggedForReview ? 'Flagged for review' : 'Mark for review'}>
                      <IconButton size="small" color={o.flaggedForReview ? 'warning' : 'default'}>
                        <Flag />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </TabPanel>

      {/* Tab 3: Completeness */}
      <TabPanel value={activeTab} index={2}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>Data Completeness by District</Typography>
                <ResponsiveContainer width="100%" height={350}>
                  <BarChart data={completenessData} margin={{ bottom: 60 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E8EDF2" />
                    <XAxis dataKey="district" angle={-45} textAnchor="end" fontSize={11} />
                    <YAxis domain={[0, 100]} fontSize={11} />
                    <RechartsTooltip />
                    <Bar dataKey="completeness" radius={[4, 4, 0, 0]}>
                      {completenessData.map((entry, index) => (
                        <Cell key={index} fill={getScoreColor(entry.completeness)} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card variant="outlined" sx={{ mb: 2 }}>
              <CardContent>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>Completeness Summary</Typography>
                {completenessData.sort((a, b) => b.completeness - a.completeness).map((d, i) => (
                  <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Typography variant="caption" sx={{ minWidth: 100, fontSize: '0.7rem' }}>{d.district}</Typography>
                    <LinearProgress
                      variant="determinate"
                      value={d.completeness}
                      sx={{
                        flex: 1, height: 8, borderRadius: 4, backgroundColor: '#E8EDF2',
                        '& .MuiLinearProgress-bar': { backgroundColor: getScoreColor(d.completeness), borderRadius: 4 },
                      }}
                    />
                    <Typography variant="caption" sx={{ fontWeight: 600, minWidth: 35, textAlign: 'right' }}>
                      {d.completeness}%
                    </Typography>
                  </Box>
                ))}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </TabPanel>

      {/* Tab 4: Quality Radar */}
      <TabPanel value={activeTab} index={3}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>Quality Dimensions Radar</Typography>
                <ResponsiveContainer width="100%" height={350}>
                  <RadarChart data={radarData}>
                    <PolarGrid stroke="#E8EDF2" />
                    <PolarAngleAxis dataKey="dimension" fontSize={12} />
                    <PolarRadiusAxis angle={90} domain={[0, 100]} fontSize={10} />
                    <Radar name="Score" dataKey="score" stroke="#147CD7" fill="#147CD7" fillOpacity={0.3} strokeWidth={2} />
                  </RadarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>Quality Assessment Details</Typography>
                {qualityDimensions.map((dim, i) => (
                  <Box key={i} sx={{ mb: 2, p: 1.5, backgroundColor: '#F8F9FA', borderRadius: 1, borderLeft: `4px solid ${getScoreColor(dim.score)}` }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>{dim.name}</Typography>
                      <Chip
                        label={`${dim.score}%`}
                        size="small"
                        sx={{ backgroundColor: getScoreColor(dim.score), color: '#fff', fontWeight: 700 }}
                      />
                    </Box>
                    <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.5 }}>
                      {dim.issues} issues found | Status: {dim.status}
                    </Typography>
                    <LinearProgress
                      variant="determinate"
                      value={dim.score}
                      sx={{
                        height: 6, borderRadius: 3, backgroundColor: '#E8EDF2',
                        '& .MuiLinearProgress-bar': { backgroundColor: getScoreColor(dim.score), borderRadius: 3 },
                      }}
                    />
                  </Box>
                ))}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </TabPanel>
    </Box>
  );
};

export default AdvancedDataValidation;
